import { useEffect, useMemo, useState } from 'react';
import { SocketNamespace } from '@trinity/chat';
import type { Socket } from 'socket.io-client';
import { createLogger } from '../utils';
import { useGetSocket } from './useGetSocket';
import { useSetupDefaultSocketListeners } from './useSetupDefaultListeners';

export const useChatSocket = (chatIdentifier: string): Socket => {
  const [socket, setSocket] = useState<Socket>();
  const getSocket = useGetSocket(SocketNamespace.Chat);

  const logger = useMemo(() => createLogger({
    namespace: SocketNamespace.Chat,
  }), []);

  useEffect(() => {
    if (chatIdentifier) {
      logger.log(`Setting up socket for chat: ${chatIdentifier}`);

      const newSocket = getSocket();
      setSocket(newSocket);

      // Wait for connection before subscribing
      const handleConnect = () => {
        logger.log(`Socket connected, subscribing to: ${chatIdentifier}`);
        newSocket.emit('subscribe', chatIdentifier);
      };

      if (newSocket.connected) {
        handleConnect();
      } else {
        newSocket.on('connect', handleConnect);
      }

      logger.log(`Socket setup complete for: ${chatIdentifier}`);

      return () => {
        logger.log(`Cleaning up socket for chat: ${chatIdentifier}`);
        newSocket.off('connect', handleConnect);
        newSocket.emit('unsubscribe', chatIdentifier);
        newSocket.disconnect();
      };
    }
  }, [chatIdentifier, getSocket, logger]);

  useSetupDefaultSocketListeners(socket, SocketNamespace.Chat);

  return socket;
};